import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { MOBILE_NAV_HEIGHT } from '#components/mobile/MobileNavTabs';
import { Cargando } from '#components/mobile/ui/Cargando';
import { Segmentado } from '#components/mobile/ui/Segmentado';
import {
  color,
  espacio,
  radio,
  TACTIL,
  texto,
} from '#components/mobile/ui/tokens';
import { Page } from '#components/Page';
import { useNavigate } from '#hooks/useNavigate';
import { pedirPuente } from '#uso';

import { EncuestaPuente } from './EncuestaPuente';
import { FichaPersona } from './FichaPersona';
import {
  CabeceraPuente,
  Dato,
  FilaPeticion,
  Inicial,
  ListaRecuentos,
  Seccion,
  Semaforito,
} from './piezas';
import { Plazas } from './Plazas';
import {
  coincide,
  correosDe,
  haceDias,
  nombreDe,
  nombreFallo,
  nombrePantalla,
  nombrePlan,
  REFRESCO_MS,
  tamano,
} from './puente';
import type { FilaPersona, ResumenPuente, Semaforo } from './puente';
import { Vigia } from './Vigia';

/** Ruta del Puente de mando (solo admin, enlace en Ajustes). */
export const RUTA_PUENTE = '/puente';

type Pestana = 'resumen' | 'personas' | 'uso' | 'encuesta' | 'vigia';
type Filtro = 'todas' | Semaforo | 'sin-permiso';

/**
 * Puente de mando (Vadik, 27-oct-2026): quién usa Rumbo, quién se enfría,
 * qué falla y qué no usa nadie, en vivo (se pide cada minuto mientras está
 * abierto). Solo para quien administra el servidor: el servidor responde 403
 * al resto. Nunca hay importes, cuentas ni nada que la persona escriba.
 */
export function PuentePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [datos, setDatos] = useState<ResumenPuente | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cargadoA, setCargadoA] = useState<number | null>(null);
  const [pestana, setPestana] = useState<Pestana>('resumen');
  const [fichaId, setFichaId] = useState<string | null>(null);
  const [vuelta, setVuelta] = useState(0);
  const [ahora, setAhora] = useState(() => Date.now());

  useEffect(() => {
    let vivo = true;
    const cargar = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }
      pedirPuente<ResumenPuente>('/')
        .then(d => {
          if (vivo) {
            setDatos(d);
            setError(null);
            setCargadoA(Date.now());
          }
        })
        .catch((e: Error) => {
          if (vivo) {
            setError(e.message);
          }
        });
    };
    cargar();
    const id = window.setInterval(cargar, REFRESCO_MS);
    const reloj = window.setInterval(() => setAhora(Date.now()), 15_000);
    document.addEventListener('visibilitychange', cargar);
    return () => {
      vivo = false;
      window.clearInterval(id);
      window.clearInterval(reloj);
      document.removeEventListener('visibilitychange', cargar);
    };
  }, [vuelta]);

  const actualizar = () => setVuelta(v => v + 1);

  if (fichaId) {
    return (
      <FichaPersona
        id={fichaId}
        onVolver={() => {
          setFichaId(null);
          actualizar();
        }}
      />
    );
  }

  const segundos = cargadoA ? Math.round((ahora - cargadoA) / 1000) : null;

  return (
    <Page
      padding={0}
      header={
        <CabeceraPuente
          titulo={t('Command bridge')}
          subtitulo={
            segundos == null
              ? t('Loading…')
              : segundos < 60
                ? t('Live · updated just now')
                : t('Live · updated {{count}} min ago', {
                    count: Math.round(segundos / 60),
                  })
          }
          onVolver={() => void navigate(-1)}
          onActualizar={actualizar}
        />
      }
    >
      <View
        data-testid="puente"
        style={{
          gap: espacio.tarjetas,
          padding: `${espacio.fila}px ${espacio.margen}px`,
          paddingBottom: MOBILE_NAV_HEIGHT + espacio.margen,
          flexShrink: 0,
        }}
      >
        <Segmentado<Pestana>
          etiqueta={t('Command bridge')}
          opciones={[
            { valor: 'resumen', texto: t('Summary') },
            { valor: 'personas', texto: t('People') },
            { valor: 'uso', texto: t('Use and failures') },
            { valor: 'encuesta', texto: t('Survey') },
            { valor: 'vigia', texto: t('Lookout') },
          ]}
          valor={pestana}
          onChange={setPestana}
          testId="puente-pestana"
          estirar
          alto={TACTIL}
        />
        {error === 'forbidden' ? (
          <Aviso>
            <Trans>Only the person who runs Rumbo can see this.</Trans>
          </Aviso>
        ) : error && !datos ? (
          <Aviso>
            <Trans>
              The server could not be reached. Try again in a while.
            </Trans>
          </Aviso>
        ) : pestana === 'vigia' ? (
          <Vigia />
        ) : !datos ? (
          <Cargando />
        ) : pestana === 'resumen' ? (
          <Resumen datos={datos} onPersona={setFichaId} onCambio={actualizar} />
        ) : pestana === 'personas' ? (
          <Personas personas={datos.personas} onPersona={setFichaId} />
        ) : pestana === 'encuesta' ? (
          <EncuestaPuente datos={datos} onPersona={setFichaId} />
        ) : (
          <UsoYFallos datos={datos} onCambio={actualizar} />
        )}
        <Text
          style={{ ...texto.secundario, color: color.fg3, textAlign: 'center' }}
        >
          <Trans>
            Amounts, accounts and anything people type are never shown.
          </Trans>
        </Text>
      </View>
    </Page>
  );
}

function Resumen({
  datos,
  onPersona,
  onCambio,
}: {
  datos: ResumenPuente;
  onPersona: (id: string) => void;
  onCambio: () => void;
}) {
  const { t } = useTranslation();
  const { totales } = datos;
  const escribir = datos.personas.filter(
    p => p.activa && p.rol !== 'ADMIN' && p.semaforo !== 'verde',
  );
  const nuevas = datos.peticiones.filter(p => p.estado === 'nueva');
  return (
    <>
      <View
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: 8,
        }}
      >
        <Cifra titulo={t('People')} valor={totales.personas} />
        <Cifra
          titulo={t('Used it this week')}
          valor={totales.usan7}
          tono="ok"
        />
        <Cifra
          titulo={t('Cooling off')}
          valor={totales.enfriandose}
          tono={totales.enfriandose ? 'warn' : undefined}
        />
        <Cifra
          titulo={t('In red')}
          valor={totales.enRojo}
          tono={totales.enRojo ? 'bad' : undefined}
        />
        <Cifra
          titulo={t('Failures (7 days)')}
          valor={totales.fallos7}
          tono={totales.fallos7 ? 'bad' : undefined}
        />
        <Cifra titulo={t('Pay')} valor={totales.pagan} />
      </View>

      {datos.plazas && (
        <Plazas
          key={datos.plazas.plazas}
          datos={datos.plazas}
          ahora={datos.ahora}
          onCambio={onCambio}
        />
      )}

      <Seccion titulo={t('Who to write to')}>
        {escribir.length === 0 ? (
          <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
            <Trans>Everyone is sailing fine.</Trans>
          </Text>
        ) : (
          escribir.map(p => (
            <FilaPersonaLista
              key={p.id}
              p={p}
              onPress={() => onPersona(p.id)}
            />
          ))
        )}
      </Seccion>

      <Seccion
        titulo={t('Messages from people ({{count}} new)', {
          count: nuevas.length,
        })}
      >
        {nuevas.length === 0 ? (
          <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
            <Trans>Nothing new.</Trans>
          </Text>
        ) : (
          nuevas
            .slice(0, 5)
            .map(p => <FilaPeticion key={p.id} p={p} onPersona={onPersona} />)
        )}
      </Seccion>

      <Seccion titulo={t('Server')}>
        {datos.servidor.capacidad && (
          <>
            <Dato
              titulo={t('Registered people')}
              valor={datos.servidor.capacidad.registradas}
            />
            <Dato
              titulo={t('Room for more (estimate)')}
              valor={
                datos.servidor.capacidad.caben == null
                  ? '—'
                  : datos.servidor.capacidad.limita === 'memoria'
                    ? t('~{{count}} more · memory runs out first', {
                        count: datos.servidor.capacidad.caben,
                      })
                    : t('~{{count}} more · disk runs out first', {
                        count: datos.servidor.capacidad.caben,
                      })
              }
              tono={
                datos.servidor.capacidad.caben != null &&
                datos.servidor.capacidad.caben < 10
                  ? 'bad'
                  : undefined
              }
            />
          </>
        )}
        <Dato
          titulo={t('Free disk')}
          valor={
            datos.servidor.disco
              ? `${tamano(datos.servidor.disco.libre)} / ${tamano(datos.servidor.disco.total)}`
              : '—'
          }
        />
        <Dato
          titulo={t('Memory Rumbo uses')}
          valor={
            datos.servidor.memoriaTotal
              ? `${tamano(datos.servidor.memoria)} / ${tamano(datos.servidor.memoriaTotal)}`
              : tamano(datos.servidor.memoria)
          }
        />
        <Dato
          titulo={t('Running for')}
          valor={t('{{count}} hours', {
            count: Math.floor(datos.servidor.enMarcha / 3600),
          })}
        />
        <Dato
          titulo={t('Gave usage permission')}
          valor={`${totales.conPermisoUso} / ${totales.personas}`}
        />
      </Seccion>
    </>
  );
}

function Personas({
  personas,
  onPersona,
}: {
  personas: FilaPersona[];
  onPersona: (id: string) => void;
}) {
  const { t } = useTranslation();
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState<Filtro>('todas');
  const [copiado, setCopiado] = useState(false);

  const lista = personas.filter(
    p =>
      coincide(p, busqueda) &&
      (filtro === 'todas' ||
        (filtro === 'sin-permiso' ? !p.permisoUso : p.semaforo === filtro)),
  );

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(correosDe(lista));
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 2500);
    } catch {
      setCopiado(false);
    }
  };

  const filtros: Array<{ valor: Filtro; texto: string }> = [
    { valor: 'todas', texto: t('All') },
    { valor: 'verde', texto: t('Fine') },
    { valor: 'ambar', texto: t('Cooling off') },
    { valor: 'rojo', texto: t('In red') },
    { valor: 'sin-permiso', texto: t('No permission') },
  ];

  return (
    <>
      <input
        type="search"
        aria-label={t('Search by name or email')}
        placeholder={t('Search by name or email')}
        value={busqueda}
        onChange={e => setBusqueda(e.target.value)}
        data-testid="puente-buscar"
        style={{
          width: '100%',
          minHeight: TACTIL,
          borderRadius: radio.boton,
          backgroundColor: color.surface,
          border: `1px solid ${color.line}`,
          padding: '0 14px',
          fontSize: 16,
          color: color.fg,
          boxSizing: 'border-box',
          fontFamily: 'inherit',
        }}
      />
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
        {filtros.map(f => (
          <Button
            key={f.valor}
            variant="bare"
            aria-pressed={filtro === f.valor}
            onPress={() => setFiltro(f.valor)}
            style={{
              minHeight: 36,
              padding: '0 12px',
              borderRadius: 18,
              fontSize: 13,
              fontWeight: 800,
              backgroundColor:
                filtro === f.valor ? color.accent : color.surface,
              color: filtro === f.valor ? color.accentInk : color.fg,
              border: `1px solid ${filtro === f.valor ? color.accent : color.line}`,
            }}
          >
            {f.texto}
          </Button>
        ))}
      </View>
      <Seccion
        titulo={t('{{count}} people', { count: lista.length })}
        derecha={
          <Button
            variant="bare"
            onPress={() => void copiar()}
            isDisabled={lista.length === 0}
            style={{
              minHeight: 36,
              padding: '0 10px',
              borderRadius: radio.boton,
              fontSize: 13,
              fontWeight: 800,
              color: color.accent,
            }}
          >
            {copiado ? t('Copied') : t('Copy emails')}
          </Button>
        }
      >
        {lista.map(p => (
          <FilaPersonaLista key={p.id} p={p} onPress={() => onPersona(p.id)} />
        ))}
      </Seccion>
    </>
  );
}

function UsoYFallos({
  datos,
  onCambio,
}: {
  datos: ResumenPuente;
  onCambio: () => void;
}) {
  const { t } = useTranslation();
  const { uso } = datos;
  return (
    <>
      <Seccion titulo={t('Failures in the last 7 days')}>
        <ListaRecuentos
          filas={datos.fallos}
          nombre={c => nombreFallo(t, c)}
          vacio={t('No failures. Smooth sailing.')}
          tono="bad"
        />
      </Seccion>
      <Seccion titulo={t('Most used screens (30 days)')}>
        <ListaRecuentos
          filas={uso.pantallasMas}
          nombre={c => nombrePantalla(t, c)}
        />
      </Seccion>
      <Seccion titulo={t('Least used screens (30 days)')}>
        <ListaRecuentos
          filas={uso.pantallasMenos}
          nombre={c => nombrePantalla(t, c)}
        />
      </Seccion>
      <Seccion titulo={t('Compass options')}>
        <ListaRecuentos filas={uso.brujula} nombre={c => c} />
      </Seccion>
      <Seccion titulo={t('Windows opened')}>
        <ListaRecuentos filas={uso.ventanas} nombre={c => c} />
      </Seccion>
      <Seccion titulo={t('Cards')}>
        <ListaRecuentos filas={uso.tarjetas} nombre={c => c} />
      </Seccion>
      <Seccion titulo={t('What people do')}>
        <ListaRecuentos filas={uso.acciones} nombre={c => c} />
      </Seccion>
      <Seccion titulo={t('Versions in use')}>
        <ListaRecuentos filas={uso.versiones} nombre={c => `v${c}`} />
      </Seccion>
      <Seccion titulo={t('Messages from people')}>
        {datos.peticiones.length === 0 ? (
          <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
            <Trans>Nobody has written yet.</Trans>
          </Text>
        ) : (
          datos.peticiones.map(p => (
            <FilaPeticion key={p.id} p={p} onCambio={onCambio} />
          ))
        )}
      </Seccion>
    </>
  );
}

function Cifra({
  titulo,
  valor,
  tono,
}: {
  titulo: string;
  valor: number;
  tono?: 'ok' | 'warn' | 'bad';
}) {
  return (
    <View
      style={{
        backgroundColor: color.surface,
        borderRadius: 14,
        padding: 10,
        minWidth: 0,
        boxShadow: '0 1px 2px rgba(20,33,61,0.08)',
      }}
    >
      <Text style={{ fontSize: 11.5, fontWeight: 700, color: color.fg3 }}>
        {titulo}
      </Text>
      <Text
        style={{
          fontSize: 24,
          fontWeight: 800,
          fontVariantNumeric: 'tabular-nums',
          color: tono ? color[tono] : color.fg,
        }}
      >
        {valor}
      </Text>
    </View>
  );
}

function FilaPersonaLista({
  p,
  onPress,
}: {
  p: FilaPersona;
  onPress: () => void;
}) {
  const { t } = useTranslation();
  const detalle = [
    p.base === 'nunca'
      ? t('Never used it')
      : t('Last use: {{cuando}}', { cuando: haceDias(t, p.diasSinUsar) }),
    p.fallos7 > 0 ? t('{{count}} failures', { count: p.fallos7 }) : null,
    nombrePlan(t, p),
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Button
      variant="bare"
      onPress={onPress}
      data-testid={`puente-persona-${p.id}`}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        minHeight: TACTIL + 8,
        padding: '4px 0',
        justifyContent: 'flex-start',
        textAlign: 'left',
        opacity: p.activa ? 1 : 0.55,
      }}
    >
      <Inicial p={p} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
          <Semaforito semaforo={p.semaforo} />
          <Text
            style={{
              ...texto.fila,
              color: color.fg,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {nombreDe(p)}
          </Text>
        </View>
        <Text
          style={{
            ...texto.secundario,
            color: color.fg3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {p.correo}
        </Text>
        <Text style={{ ...texto.secundario, color: color.fg2 }}>{detalle}</Text>
      </View>
    </Button>
  );
}

function Aviso({ children }: { children: ReactNode }) {
  return (
    <View
      style={{
        backgroundColor: color.warnSoft,
        borderRadius: 14,
        padding: 14,
      }}
    >
      <Text style={{ ...texto.cuerpo, color: color.fg }}>{children}</Text>
    </View>
  );
}
